// Assets
import locationPin from './../assets/routes_map_view/location-pin.svg';
import hospitalPin from './../assets/routes_map_view/hospital-pin.svg';

// Interfaces
import { Location } from '../interfaces/Location';
import { GeocoderResponse } from '../interfaces/GeocoderResponse';

export const fetchGeolocationAddress = async (address: string, GOOGLE_API_KEY: string) => {
    return await fetch(`https://maps.google.com/maps/api/geocode/json?address=${address}&sensor=false&key=${GOOGLE_API_KEY}`)
        .then(response => response.json())
        .then((data: GeocoderResponse) => {
            return data;
        })
}

// returns a Promise of the GeolocationPosition of the user
export const getPosition = (): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error("Geolocation is not supported by this browser."));
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (pos: GeolocationPosition) => resolve(pos),
            (err: GeolocationPositionError) => reject(err),
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
            }
        );
    });
};

const getGeolocationErrorMessage = (error: GeolocationPositionError) => {
    switch (error.code) {
        case error.PERMISSION_DENIED:
            return "User denied the request for Geolocation. Please check you browser settings to allow the site.";
        case error.POSITION_UNAVAILABLE:
            return "Location information is unavailable.";

        case error.TIMEOUT:
            return "The request to get user location timed out.";
        default:
            return ("An unknown geolocation error occurred.");
    }
}

function isGeolocationError(error: any): error is GeolocationPositionError {
    return error && typeof error.code === "number" && typeof error.message === "string";
}

// returns the Latitude and Longitude of the user.
export const getUserGeolocalization: () => Promise<Location | undefined> = async () => {

    try {
        const position = await getPosition();
        if (!position) return;
        return {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
        };
    } catch (error) {
        if (isGeolocationError(error)) {
            alert(getGeolocationErrorMessage(error));
        }
        else console.error("Geolocation error:", error);

        // You can optionally return a fallback location here, or let it be undefined
        return undefined;
    }
};

// creates a marker in the map from the position
const createMarker = (
    position: google.maps.LatLng,
    map: google.maps.Map,
    url?: string,
    label?: string,
) => {
    const marker = new google.maps.Marker({
        position: position,
        map: map,
        icon: url !== undefined ? {
            url: url,
            scaledSize: new google.maps.Size(50, 50)
        } : undefined,
        label: label !== undefined ? label : undefined,
    });
    return marker;
};

export const getMapsRoute = (
    start: Location,
    locations: Location[],
    finish: Location,
    routeOptions?: Partial<google.maps.DirectionsRequest>
): Promise<google.maps.DirectionsResult> => {
    return new Promise((resolve, reject) => {

        const origin = new google.maps.LatLng(start.lat, start.lng);
        const destination = new google.maps.LatLng(finish.lat, finish.lng);
        const waypoints = locations.map(loc => ({
            location: new google.maps.LatLng(loc.lat, loc.lng),
            stopover: true,
        }));

        const directionsService = new google.maps.DirectionsService();

        directionsService.route(
            {
                origin,
                destination,
                waypoints,
                optimizeWaypoints: true,
                travelMode: google.maps.TravelMode.DRIVING,
                ...routeOptions,
            },
            (result, status) => {
                if (status === google.maps.DirectionsStatus.OK && result?.routes?.[0]) {
                    resolve(result);
                } else {
                    console.error("Failed to optimize route:", status, result);
                    reject(new Error("Failed to optimize route"));
                }
            }
        );
    });
};

export const createOrderedMarkers = (
    map: google.maps.Map,
    origin: Location,
    locations: Location[],
    destination: Location
) => {

    const markers: google.maps.Marker[] = [];

    // Origin
    markers.push(createMarker(new google.maps.LatLng(origin), map, locationPin, undefined));

    // Ordered stops
    locations.forEach((loc, i) => {
        markers.push(createMarker(new google.maps.LatLng(loc), map, undefined, `${i + 1}`));
    });

    // Destination
    markers.push(createMarker(new google.maps.LatLng(destination), map, hospitalPin, undefined));

    return markers;

};

export const renderRouteOnMap = (
    map: google.maps.Map,
    result: google.maps.DirectionsResult
) => {
    const renderer = new google.maps.DirectionsRenderer({
        suppressMarkers: true, // or true if you're using custom markers
    });
    renderer.setMap(map);
    renderer.setDirections(result);
    return renderer;
};